import { Divider, Input, ScrollShadow } from '@nextui-org/react';
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import { HeaderSearchIcon, SelectCollectionGreenIcon } from '@/assets';
import { Buttons, Images } from '@/components';
import NoData from '@/components/NoData';
import type { AssetData } from '@/contents/user-profile/content/artwork/useUserArwork';
import useUserArtwork from '@/contents/user-profile/content/artwork/useUserArwork';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { useAuth } from '@/provider/UserProvider';
import { addAssetToCollection } from '@/services/collection';
import type { CollectionData } from '@/types/collection';

interface ModelSelectArtworkProps {
  defaultValue: number[];
  onClose: () => void;
  collection: CollectionData;
  assetDataIds: number[];
  assetDataRef: any;
  setAssetData: Dispatch<SetStateAction<AssetData[]>>;
}

const ModelSelectArtwork: React.FC<ModelSelectArtworkProps> = ({
  defaultValue,
  onClose,
  collection,
  assetDataIds,
  assetDataRef,
  setAssetData,
}) => {
  const [select, setSelect] = useState(defaultValue);
  const { auth } = useAuth();
  const scrollRef = useRef(null);
  const { isLoadMore } = useInfiniteScroll(scrollRef);
  const { currentPage, artworks, setSearch, loadMore, loading } = useUserArtwork(
    auth.user.publicAddress,
    true,
  );

  const handleSelect = (item: any) => {
    if (select.includes(item.id)) {
      const filter = select.filter((chooser) => chooser !== item.id);
      setSelect(filter);
    } else setSelect((select) => [...select, item.id]);
  };

  const handleAddCollection = async (onClose: any) => {
    if (select?.length < 1) return;
    const res = await addAssetToCollection(collection.id, { nftIds: select });

    if (res.status === 200) {
      const filterAir = artworks.filter((item) => select.includes(item.id));
      assetDataRef.current = assetDataRef.current.concat(filterAir);
      setAssetData(assetDataRef.current);
      onClose();
    }
  };

  useEffect(() => {
    const isLoadMoreData = !loading && isLoadMore && Boolean(loadMore);
    if (isLoadMoreData) loadMore(currentPage + 1);
  }, [isLoadMore]);

  return (
    <div className="w-full">
      <div className="my-7 flex w-full items-center justify-center px-7 sm:flex-col sm:items-start">
        <p className="flex-none text-24 font-semibold md:text-18 sm:mb-3">Select artwork</p>
        <Input
          classNames={{
            base: 'items-center ml-3 sm:ml-0 md:w-full',
            mainWrapper: 'md:!w-full',
            input: ['dark:placeholder:text-white font-normal text-14 pl-1', 'rounded-[100px]'],
            innerWrapper: ['bg-transparent', 'rounded-[100px]'],
            inputWrapper: ['bg-gray-50', 'rounded-[100px]', 'w-[400px] md:!w-full'],
          }}
          startContent={<HeaderSearchIcon />}
          placeholder="Search in Artworks"
          onValueChange={(e) => setSearch(e)}
        />
      </div>
      <Divider className="bg-gray-100" />
      <ScrollShadow
        ref={scrollRef}
        hideScrollBar
        className="h-[calc(80vh-195px)] overflow-hidden overflow-y-auto sm:h-[calc(80vh-214px)]"
      >
        {!artworks.some((item) => !assetDataIds.includes(item?.id) && !item?.collectionId) && (
          <NoData />
        )}
        <div className="grid grid-cols-5 justify-center gap-[12px] px-7 py-3 xl:grid-cols-4 lg:!grid-cols-3 md:mx-3 md:!grid-cols-2 sm:!grid-cols-1">
          {artworks?.length !== 0 &&
            artworks.map(
              (item) =>
                !assetDataIds.includes(item?.id) &&
                !item?.collectionId && (
                  <div
                    aria-checked={select.includes(item.id)}
                    key={item.id}
                    onClick={() => handleSelect(item)}
                    className="shadow-card-transparent relative h-fit w-full cursor-pointer rounded-[16px] border-green-500 p-2 aria-checked:border-[3px]"
                  >
                    {select.includes(item.id) && (
                      <div className="absolute right-[-15px] top-[-15px] z-99">
                        <SelectCollectionGreenIcon />
                      </div>
                    )}
                    <Images
                      alt={item.url}
                      src={item.url}
                      imageWrapper="rounded-[16px] mb-2 !h-[180px]"
                      className="min-h-[180px] rounded-[16px] object-cover object-top"
                    />
                    <div className="flex items-center justify-between">
                      <div className="ml-2 flex flex-col">
                        <span className="text-14 font-normal text-gray-500">Name</span>
                        <span className="text-13 font-semibold">
                          {item?.name?.length && item?.name?.length > 10
                            ? `${item?.name?.slice(0, 10)}...`
                            : item?.name || '--'}
                        </span>
                      </div>
                      <div className="mr-2 flex flex-col">
                        <span className="text-14 font-normal text-gray-500">Model</span>
                        <span className="text-13 font-semibold">{item?.model?.name || '--'}</span>
                      </div>
                    </div>
                  </div>
                ),
            )}
        </div>
      </ScrollShadow>
      {artworks?.length !== 0 &&
        artworks.some((item) => !assetDataIds.includes(item?.id) && !item?.collectionId) && (
          <div className="shadow-scroll flex h-[82px] items-center justify-end px-10 sm:h-[60px]">
            <Buttons
              onClick={() => handleAddCollection(onClose)}
              className="ml-3 w-[235px] rounded-full md:w-fit"
              typeBtn="primary"
            >
              <span className="text-15 font-semibold text-white">Confirm</span>
            </Buttons>
          </div>
        )}
    </div>
  );
};

export default ModelSelectArtwork;
